import React, { useRef, useEffect, useState } from "react";
import "./contextMenu.css";

/**
 * A custom context menu component that displays at the specified position with provided menu options.
 * The menu automatically adjusts its position to open upward when there is not enough space below.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Array} props.menu - Array of menu option objects
 * @param {string} props.menu[].id - Unique identifier for the menu option
 * @param {string} props.menu[].label - Display text for the menu option
 * @param {React.ReactNode} [props.menu[].icon] - Optional icon component to display with the menu option
 * @param {Function|boolean} [props.menu[].disabled] - Boolean or function that determines if option is disabled
 * @param {Function} [props.menu[].handle] - Function to execute when option is selected, receives data as parameter
 * @param {Object} props.data - Data to be passed to the menu option handlers
 * @param {boolean} props.visible - Whether the context menu should be visible
 * @param {Object} props.position - Position where the menu should be displayed
 * @param {number} props.position.x - X-coordinate for the menu position
 * @param {number} props.position.y - Y-coordinate for the menu position
 * @param {Function} props.onClose - Function to call when the menu should close
 * @param {string} props.theme - Theme class name to apply to the menu
 * @returns {React.ReactElement|null} The rendered context menu or null if not visible
 */
const ContextMenu = ({ menu, data, visible, position, onClose, theme }) => {
    const menuRef = useRef(null);
    const [shouldOpenUpward, setShouldOpenUpward] = useState(false);

    // Efeito para verificar se deve abrir para cima ou para baixo
    useEffect(() => {
        // Se o menu estiver visível e a posição estiver definida
        if (visible && menuRef.current && position) {
            const menuHeight = menuRef.current.getBoundingClientRect().height;
            const screenHeight = window.innerHeight;
            const availableSpaceBelow = screenHeight - position.y;

            // Se o espaço abaixo for menor que a altura do menu + margem de 20px
            setShouldOpenUpward(availableSpaceBelow < menuHeight + 20);
        }
    }, [visible, position]);

    if (!visible || !menu || !position) {
        return null;
    }
    
    /**
     * Determines if a context menu option should be disabled.
     * 
     * @param {Object} option - The context menu option to check.
     * @param {boolean|Function} option.disabled - Either a boolean value or a function that returns a boolean.
     * @returns {boolean} - Returns true if the option should be disabled, false otherwise.
     */
    const isItemDisabled = (option) => {
        if (typeof option.disabled === "function") {
            return option.disabled(data);
        }
        return option.disabled === true;
    };

    /**
     * Executes the handler function associated with a context menu option.
     * 
     * @param {Object} option - The context menu option object
     * @param {Function|undefined} option.handle - The handler function to execute when the option is selected
     * @throws {Console.warn} - Warns if option.handle is defined but not a function
     * @returns {void}
     */
    const handle = (option) => {

        if (option.handle) {
            if (typeof option.handle === "function") {
                option.handle(data);
            } else {
                console.warn(
                    "handle should be a function or undefined, but got:",
                    typeof option.handle
                );
            }
        }
    }

    return (
        <div
            ref={menuRef}
            className={`custom-context-menu ${theme}`}
            style={{
                position: "fixed",
                left: position.x,
                ...(shouldOpenUpward
                    ? { bottom: window.innerHeight - position.y, top: "auto" }
                    : { top: position.y, bottom: "auto" }),
            }}
        >
            {menu.map((option) => (
                <>
                    <div
                        key={option.id}
                        className={`custom-context-menu-item ${
                            isItemDisabled(option) ? "disabled" : ""
                        }`}
                        onClick={() => {
                            if (!isItemDisabled(option)) {
                                handle(option);
                                onClose();
                            }
                        }}
                    >
                        {option.icon && (
                            <span className="custom-context-menu-icon">
                                {option.icon}
                            </span>
                        )}
                        <span className="custom-context-menu-text">
                            {option.label}
                        </span>
                    </div>
                </>
            ))}
        </div>
    );
};

export default ContextMenu;
